import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAdminStore } from '../lib/store';
import clsx from 'clsx';
import {
  Page, PageHeader, card, cardHead, th, td, thMd, tdMd, thSm, tdSm, trHover, input, select, selectStyle,
  btnPrimary, btnSecondary, btnSm, iconBtn, iconBtnDanger, StatusChip, EmptyState,
} from '../components/ui';
import { IconPlus, IconSearch, IconEye, IconEdit, IconTrash, IconCalendar } from '../components/Icons';

const LIFF_BASE = 'https://group-booking-liff.pages.dev';

export default function Events() {
  const { events, setEvents } = useAdminStore();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    async function loadEvents() {
      try {
        const res = await api.getEvents();
        if (res.success) {
          setEvents(res.data);
        }
      } catch (error) {
        console.error('Failed to load events:', error);
      } finally {
        setLoading(false);
      }
    }

    loadEvents();
  }, [setEvents]);

  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.title.toLowerCase().includes(search.toLowerCase()) ||
      (event.titleEn?.toLowerCase().includes(search.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此活動嗎？此操作無法復原。')) return;

    try {
      await api.deleteEvent(id);
      setEvents(events.filter((e) => e.id !== id));
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('刪除失敗');
    }
  };

  const handlePublish = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      await api.updateEvent(id, { status: newStatus });
      setEvents(events.map((e) => e.id === id ? { ...e, status: newStatus } : e));
    } catch (error) {
      console.error('Failed to update event:', error);
      alert('更新失敗');
    }
  };

  const openPreview = (id: string) => {
    window.open(`${LIFF_BASE}/event/${id}?preview=true`, '_blank', 'width=414,height=896');
  };

  return (
    <Page>
      <PageHeader
        title="活動列表"
        subtitle={loading ? undefined : `共 ${events.length} 場活動`}
        actions={
          <Link to="/events/new" className={btnPrimary}>
            <IconPlus size={16} />
            新增活動
          </Link>
        }
      />

      <section className={clsx(card, 'flex flex-col')}>
        {/* Filters */}
        <div className={cardHead}>
          <div className="flex flex-wrap gap-2.5 flex-1 min-w-0">
            <div className="relative w-full sm:w-72">
              <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA3B0] pointer-events-none" />
              <input
                type="search"
                placeholder="搜尋活動名稱"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="搜尋活動"
                className={clsx(input, 'pl-9')}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="篩選狀態"
              className={select}
              style={selectStyle}
            >
              <option value="all">全部狀態</option>
              <option value="published">已發布</option>
              <option value="draft">草稿</option>
              <option value="closed">已關閉</option>
            </select>
          </div>
          <span className="tabular text-[13px] text-muted">
            {search || statusFilter !== 'all' ? `符合 ${filteredEvents.length} 場` : ''}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={th}>活動</th>
                <th className={thMd}>活動日期</th>
                <th className={thMd}>報名截止</th>
                <th className={thSm}>報名人數</th>
                <th className={thSm}>狀態</th>
                <th className={th}><span className="sr-only">操作</span></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-muted text-sm">載入中</td></tr>
              ) : filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-0">
                    <EmptyState
                      icon={<IconCalendar size={28} />}
                      title={events.length === 0 ? '尚無活動' : '沒有符合條件的活動'}
                      hint={events.length === 0 ? '建立第一場活動，報名表單就會在 LINE 上開放。' : '試試其他關鍵字或狀態。'}
                      action={events.length === 0 && (
                        <Link to="/events/new" className={btnPrimary}>
                          <IconPlus size={16} />
                          新增活動
                        </Link>
                      )}
                    />
                  </td>
                </tr>
              ) : filteredEvents.map((event) => (
                <tr key={event.id} className={trHover}>
                  <td className={clsx(td, 'max-w-[360px]')}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        aria-hidden="true"
                        className="w-2 h-9 rounded-full shrink-0"
                        style={{ background: event.theme?.primaryColor || '#CFD5DD' }}
                      />
                      <div className="flex flex-col leading-snug min-w-0">
                        <Link to={`/events/${event.id}/edit`} className="font-medium text-ink hover:text-primary truncate">
                          {event.title}
                        </Link>
                        {event.titleEn && <span className="text-xs text-muted truncate">{event.titleEn}</span>}
                        {event.eventDate && <span className="md:hidden tabular text-xs text-muted">{event.eventDate}</span>}
                        <span className="sm:hidden tabular text-xs text-muted">
                          {event.bookingCount || 0} 人{(event.confirmedCount ?? 0) > 0 && <span className="text-ok">，{event.confirmedCount} 已確認</span>}
                        </span>
                        <StatusChip status={event.status} className="sm:hidden mt-1 self-start" />
                      </div>
                    </div>
                  </td>
                  <td className={clsx(tdMd, 'tabular text-muted')}>{event.eventDate || '未設定'}</td>
                  <td className={clsx(tdMd, 'tabular text-muted')}>{event.registrationDeadline?.split('T')[0] || '未設定'}</td>
                  <td className={tdSm}>
                    <span className="tabular font-medium">{event.bookingCount || 0} 人</span>
                    {(event.confirmedCount ?? 0) > 0 && (
                      <span className="tabular text-xs text-ok block sm:inline sm:ml-2">{event.confirmedCount} 已確認</span>
                    )}
                  </td>
                  <td className={tdSm}><StatusChip status={event.status} /></td>
                  <td className={clsx(td, 'text-right')}>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handlePublish(event.id, event.status)}
                        className={clsx(btnSecondary, btnSm, 'hidden sm:inline-flex', event.status !== 'published' && 'text-ok border-[#BFE3CC] hover:bg-ok-soft')}
                      >
                        {event.status === 'published' ? '取消發布' : '發布'}
                      </button>
                      <button type="button" onClick={() => openPreview(event.id)} aria-label={`預覽 ${event.title}`} title="預覽" className={iconBtn}>
                        <IconEye size={16} />
                      </button>
                      <Link to={`/events/${event.id}/edit`} aria-label={`編輯 ${event.title}`} title="編輯" className={iconBtn}>
                        <IconEdit size={16} />
                      </Link>
                      <button type="button" onClick={() => handleDelete(event.id)} aria-label={`刪除 ${event.title}`} title="刪除" className={iconBtnDanger}>
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Page>
  );
}
